import { Controller } from "@hotwired/stimulus"

const ROW_SELECTOR = "tbody tr[data-lead-id]"
const SELECTED_CLASS = "last-opened-lead"
const WIDE_QUERY = "(min-width: 1536px)"
const LOAD_DELAY = 150
// Text fields and widgets that keep their own keys; a focused checkbox does not. Kept in sync with the keyboard shortcuts controller.
const TYPING_SELECTOR = "input:not([type='checkbox']):not([type='radio']):not([type='button']):not([type='submit']):not([type='reset']), textarea, select, [contenteditable]:not([contenteditable='false']), [role='textbox'], [role='combobox'], [role='listbox'], [role='menu']"
// Open menus and popovers get Escape for themselves. The app's dropdown menus show by dropping .hidden; popover menus
// such as the bulk actions More menu stay in the page while closed, so only :popover-open counts for them.
const OPEN_MENU_SELECTOR = "[role='menu']:not(.hidden):not([popover])"

function popupOpen() {
  if (document.querySelector(OPEN_MENU_SELECTOR)) return true
  try {
    return Boolean(document.querySelector(":popover-open"))
  } catch {
    // Browsers without the Popover API reject the selector and have no open popovers.
    return false
  }
}

// Owns the selected lead of the lead list: click, j/k and the arrow keys move it, Enter opens it, Escape clears it,
// and the sidebar shows its chat. A click in the sidebar chat hands the arrow keys to it for scrolling until the next
// click anywhere else; j and k always work. The open state lives in a cookie so the server renders it without a layout
// jump. Below 1536px the sidebar is a drawer over the table that only shows while a lead is selected; a first visit
// there starts with it closed. Expanded, the conversation stays on the left and the tab bar drives the right column.
// A lead can also be open without a row in the list: from a link to it (?lead=) or from a referral in the sidebar.
export default class extends Controller {
  static targets = ["panel", "scroller", "placeholder", "chat", "leadHeader", "leadName", "leadCompany", "tabs", "tab", "commentCount", "commentDraft", "leadStatus", "leadStatusDot", "actions", "previousButton", "nextButton", "expandButton", "resizeHandle", "decisionCount", "leadPicture"]
  static values = { cookie: String, defaultTab: String, focusLead: String, focusLeadUrl: String }

  connect() {
    this.keydown = event => this.handleKey(event)
    this.click = event => this.handleClick(event)
    this.pointerDown = event => this.trackPointer(event)
    this.morphed = () => this.refreshed()
    this.arrowKeys = true
    this.drafts = new Map()
    this.activeTab = this.defaultTabValue || "conversation"
    this.input = () => {
      this.saveDrafts()
      this.renderTabs()
    }
    this.contentObserver = new MutationObserver(() => this.renderTabs())
    this.contentObserver.observe(this.chatTarget, { childList: true, subtree: true })
    this.submitting = event => this.trackSubmission(event)
    document.addEventListener("keydown", this.keydown)
    document.addEventListener("pointerdown", this.pointerDown)
    document.addEventListener("turbo:morph", this.morphed)
    this.element.addEventListener("click", this.click)
    this.element.addEventListener("input", this.input)
    this.element.addEventListener("change", this.input)
    this.element.addEventListener("turbo:submit-start", this.submitting)
    this.selectedId = this.element.querySelector(`${ROW_SELECTOR}.${SELECTED_CLASS}`)?.dataset.leadId || null
    if (!this.isWide() && !this.hasStoredState()) this.panelTarget.removeAttribute("data-open")
    this.panelTarget.removeAttribute("data-unset")
    const width = Number(document.cookie.split("; ").find(entry => entry.startsWith("lead_sidebar_width="))?.split("=")[1])
    if (width) this.setWidth(width)
    if (this.focusLeadValue) this.focusLinkedLead()
    else this.render()
  }

  // Opens the lead a link pointed at, then drops ?lead= from the address so later redirects back here, such as after a
  // decision, do not reopen it.
  focusLinkedLead() {
    this.open(this.focusLeadValue, this.focusLeadUrlValue)
    this.rowFor(this.selectedId)?.scrollIntoView?.({ block: "nearest" })
    const url = new URL(window.location.href)
    url.searchParams.delete("lead")
    window.history.replaceState(window.history.state, "", url)
  }

  // Selects a lead whether or not the list shows it. Without a row the sidebar loads it from its URL.
  open(id, url) {
    this.detached = this.rowFor(id) ? null : { id, url }
    this.select(id)
  }

  disconnect() {
    this.stopResize()
    this.contentObserver.disconnect()
    clearTimeout(this.loadTimer)
    document.removeEventListener("keydown", this.keydown)
    document.removeEventListener("pointerdown", this.pointerDown)
    document.removeEventListener("turbo:morph", this.morphed)
    this.element.removeEventListener("click", this.click)
    this.element.removeEventListener("input", this.input)
    this.element.removeEventListener("change", this.input)
    this.element.removeEventListener("turbo:submit-start", this.submitting)
  }

  rows() {
    return Array.from(this.element.querySelectorAll(ROW_SELECTOR))
  }

  rowFor(id) {
    return id ? this.rows().find(row => row.dataset.leadId === id) : null
  }

  isWide() {
    return window.matchMedia(WIDE_QUERY).matches
  }

  isOpen() {
    return this.panelTarget.hasAttribute("data-open")
  }

  hasStoredState() {
    return document.cookie.split("; ").some(entry => entry.startsWith(`${this.cookieValue}=`))
  }

  toggle() {
    const open = this.panelTarget.toggleAttribute("data-open")
    document.cookie = `${this.cookieValue}=${open ? "open" : "closed"}; path=/; max-age=31536000; SameSite=Lax`
    // The narrow drawer has no empty state, so opening it without a selection starts at the top row.
    if (open && !this.selectedId && !this.isWide()) this.move(1)
    else this.render()
  }

  isExpanded() {
    return this.panelTarget.hasAttribute("data-expanded")
  }

  // Expanded, the conversation already fills the left column, so the right column shows the chosen tab or Decisions.
  visibleTab() {
    const active = this.activeTab || "conversation"
    return this.isExpanded() && active === "conversation" ? "decisions" : active
  }

  selectableTabs() {
    return this.tabTargets.filter(tab => !this.isExpanded() || tab.dataset.tab !== "conversation")
  }

  previous() { this.move(-1) }

  next() { this.move(1) }

  closeActions() {
    if (!this.hasActionsTarget) return
    const name = "views--components--element--dropdown"
    const dropdown = this.actionsTarget.querySelector(`[data-controller~="${name}"]`)
    if (dropdown) this.application.getControllerForElementAndIdentifier(dropdown, name)?.closeDropdown()
  }

  toggleExpanded() {
    if (!this.selectedId) return
    this.closeActions()
    const expanded = this.panelTarget.toggleAttribute("data-expanded")
    if (this.hasExpandButtonTarget) {
      const button = this.expandButtonTarget
      const label = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel
      button.setAttribute("aria-expanded", String(expanded))
      button.setAttribute("aria-label", label)
      button.title = label
    }
    this.renderTabs()
  }

  setWidth(width) {
    const maximum = Math.min(832, window.innerWidth - 32)
    const value = Math.round(Math.max(Math.min(360, maximum), Math.min(maximum, width)))
    this.panelTarget.style.setProperty("--lead-sidebar-width", `${value}px`)
    if (this.hasResizeHandleTarget) {
      this.resizeHandleTarget.setAttribute("aria-valuenow", String(value))
      this.resizeHandleTarget.setAttribute("aria-valuemax", String(maximum))
    }
    document.cookie = `lead_sidebar_width=${value}; path=/; max-age=31536000; SameSite=Lax`
  }

  startResize(event) {
    if (event.button !== 0 || this.panelTarget.hasAttribute("data-expanded")) return
    event.preventDefault()
    this.closeActions()
    const startWidth = this.panelTarget.getBoundingClientRect().width
    const startX = event.clientX
    this.resizing = event => this.setWidth(startWidth + startX - event.clientX)
    this.resizeEnd = () => this.stopResize()
    window.addEventListener("pointermove", this.resizing)
    window.addEventListener("pointerup", this.resizeEnd)
    window.addEventListener("pointercancel", this.resizeEnd)
  }

  stopResize() {
    if (!this.resizing) return
    window.removeEventListener("pointermove", this.resizing)
    window.removeEventListener("pointerup", this.resizeEnd)
    window.removeEventListener("pointercancel", this.resizeEnd)
    this.resizing = null
  }

  resizeKeydown(event) {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return
    event.preventDefault()
    this.setWidth(this.panelTarget.getBoundingClientRect().width + (event.key === "ArrowLeft" ? 32 : -32))
  }

  chooseTab(event) {
    if (this.isExpanded() && event.currentTarget.dataset.tab === "conversation") return
    this.activeTab = event.currentTarget.dataset.tab
    this.renderTabs()
  }

  tabKeydown(event) {
    const tabs = this.selectableTabs()
    const index = tabs.indexOf(event.currentTarget)
    let next
    if (event.key === "ArrowRight") next = (index + 1) % tabs.length
    else if (event.key === "ArrowLeft") next = (index + tabs.length - 1) % tabs.length
    else if (event.key === "Home") next = 0
    else if (event.key === "End") next = tabs.length - 1
    else return
    event.preventDefault()
    this.activeTab = tabs[next].dataset.tab
    this.renderTabs()
    tabs[next].focus()
  }

  renderTabs() {
    const active = this.visibleTab()
    const expanded = this.isExpanded()
    for (const tab of this.tabTargets || []) {
      const selected = tab.dataset.tab === active
      tab.setAttribute("aria-selected", String(selected))
      tab.tabIndex = selected ? 0 : -1
      if (tab.dataset.tab === "conversation") tab.setAttribute("aria-disabled", String(expanded))
    }
    this.chatTarget.querySelectorAll("[data-sidebar-tab-panel]").forEach(panel => {
      const name = panel.dataset.sidebarTabPanel
      panel.hidden = name !== active && !(expanded && name === "conversation")
    })
    const decisions = this.chatTarget.querySelector("[data-sidebar-decisions]")
    if (this.hasDecisionCountTarget) {
      this.decisionCountTarget.textContent = String(decisions?.querySelectorAll("[data-sidebar-approval], [data-referral-decision], [data-sidebar-followup]").length || 0)
    }
    if (this.hasCommentCountTarget) {
      const count = this.chatTarget.querySelectorAll("[id^='sidebar-lead-comment-']:not([id^='sidebar-lead-comment-form-'])").length
      this.commentCountTarget.textContent = String(count)
    }
    if (this.hasCommentDraftTarget) {
      this.commentDraftTarget.hidden = !this.chatTarget.querySelector("textarea[name='lead_comment[body]']")?.value.trim()
    }
    if (this.pendingHistoryScroll && this.conversationVisible()) this.scrollToLatest()
  }

  trackPointer(event) {
    this.arrowKeys = !this.scrollerTarget.contains(event.target)
  }

  handleClick(event) {
    if (event.button !== 0 || event.shiftKey || event.metaKey || event.ctrlKey || event.altKey) return
    if (event.target.closest("input[type=checkbox]")) return
    const leadLink = event.target.closest("a[data-sidebar-lead-id]")
    if (leadLink && this.panelTarget.contains(leadLink)) {
      event.preventDefault()
      this.open(leadLink.dataset.sidebarLeadId, leadLink.dataset.sidebarLeadUrl)
      return
    }
    const showTab = event.target.closest("[data-sidebar-show-tab]")
    if (showTab && this.panelTarget.contains(showTab)) {
      this.activeTab = showTab.dataset.sidebarShowTab
      this.renderTabs()
      return
    }
    const row = event.target.closest(ROW_SELECTOR)
    if (row) {
      const link = event.target.closest("a")
      if (link?.getAttribute("href") === row.dataset.leadUrl) event.preventDefault()
      this.select(row.dataset.leadId)
    }
  }

  handleKey(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return
    if (event.key === "Escape" && this.hasActionsTarget && this.actionsTarget.querySelector(OPEN_MENU_SELECTOR)) {
      event.preventDefault()
      this.closeActions()
      return
    }
    if (event.target.isContentEditable || event.target.closest?.(TYPING_SELECTOR)) return
    if (document.querySelector("dialog[open]")) return

    const action = this.actionFor(event)
    if (!action) return
    event.preventDefault()
    action()
  }

  actionFor(event) {
    if (event.shiftKey) return null
    switch (event.key) {
      case "ArrowDown":
        return this.arrowKeys ? () => this.move(1) : null
      case "j":
        return () => this.move(1)
      case "ArrowUp":
        return this.arrowKeys ? () => this.move(-1) : null
      case "k":
        return () => this.move(-1)
      case "s":
        return () => this.toggle()
      case "Enter":
        // Enter on a focused link or button keeps its own meaning.
        if (!this.selectedId || event.target.closest?.("a, button")) return null
        return () => this.toggleExpanded()
      case "Escape":
        if (popupOpen()) return null
        if (this.isExpanded()) return () => this.toggleExpanded()
        if (this.isOpen()) return () => this.toggle()
        return this.selectedId ? () => this.select(null) : null
      default:
        return null
    }
  }

  // Without a selection both directions start at the top row.
  move(direction) {
    const rows = this.rows()
    if (rows.length === 0) return
    const index = rows.indexOf(this.rowFor(this.selectedId))
    const next = index === -1 ? rows[0] : rows[Math.max(0, Math.min(rows.length - 1, index + direction))]
    if (document.activeElement && next.closest("table")?.contains(document.activeElement)) document.activeElement.blur()
    this.select(next.dataset.leadId)
    const header = next.closest("table")?.tHead
    next.style.scrollMarginTop = `${(header?.offsetHeight || 0) + 16}px`
    next.scrollIntoView({ block: "nearest", inline: "nearest" })
  }

  // A morph refresh (bulk actions redirect back to this page) resets the sidebar
  // and rows to the server HTML but keeps this controller, so draw it again.
  refreshed() {
    clearTimeout(this.loadTimer)
    this.loadedId = null
    if (this.selectedId && !this.rowFor(this.selectedId) && !this.detachedLead()) {
      const rows = this.rows()
      this.selectedId = rows[Math.min(this.selectedIndex || 0, rows.length - 1)]?.dataset.leadId || null
    }
    this.render()
  }

  select(id) {
    if (id && !this.isOpen()) {
      this.panelTarget.setAttribute("data-open", "")
      document.cookie = `${this.cookieValue}=open; path=/; max-age=31536000; SameSite=Lax`
    }
    this.saveDrafts()
    this.selectedId = id
    this.render()
  }

  draftKey(form, id = this.selectedId) {
    return `${id}:${form.getAttribute("action")}`
  }

  saveDrafts() {
    if (!this.selectedId || this.loadedId !== this.selectedId) return
    this.drafts ||= new Map()
    this.chatTarget.querySelectorAll("form").forEach(form => {
      const fields = Array.from(form.elements).filter(field => field.name && !["hidden", "submit", "button"].includes(field.type))
      if (fields.length) this.drafts.set(this.draftKey(form), fields.map(field => ({ name: field.name, value: field.value, checked: field.checked })))
    })
  }

  restoreDrafts() {
    const forms = Array.from(this.chatTarget.querySelectorAll("form"))
    const keys = new Set(forms.map(form => this.draftKey(form)))
    for (const key of this.drafts?.keys() || []) {
      if (key.startsWith(`${this.selectedId}:`) && !keys.has(key)) this.drafts.delete(key)
    }
    forms.forEach(form => {
      const draft = this.drafts?.get(this.draftKey(form))
      if (!draft) return
      draft.forEach(({ name, value, checked }) => {
        const field = form.elements.namedItem(name)
        if (!field) return
        field.value = value
        if (["checkbox", "radio"].includes(field.type)) field.checked = checked
        field.dispatchEvent(new Event("change", { bubbles: true }))
      })
    })
  }

  trackSubmission(event) {
    if (!this.panelTarget.contains(event.target)) return
    const form = event.target
    const key = this.draftKey(form)
    form.addEventListener("turbo:submit-end", event => {
      // Redirects can also report a failed referral action via flash. Keep those inputs until the card disappears.
      if (event.detail.success && !event.detail.fetchResponse?.redirected) this.drafts?.delete(key)
    }, { once: true })
  }

  render() {
    this.rows().forEach(row => row.classList.toggle(SELECTED_CLASS, row.dataset.leadId === this.selectedId))

    const row = this.rowFor(this.selectedId)
    if (row) {
      this.selectedIndex = this.rows().indexOf(row)
      this.detached = null
    }
    const lead = row ? row.dataset : this.detachedLead()
    this.panelTarget.toggleAttribute("data-selected", Boolean(lead))
    this.placeholderTarget.hidden = Boolean(lead)
    this.leadHeaderTarget.hidden = !lead
    if (this.hasPreviousButtonTarget) this.previousButtonTarget.disabled = !row || this.selectedIndex === 0
    if (this.hasNextButtonTarget) this.nextButtonTarget.disabled = !row || this.selectedIndex === this.rows().length - 1
    if (this.hasExpandButtonTarget) this.expandButtonTarget.disabled = !lead
    if (this.hasTabsTarget) this.tabsTarget.hidden = !lead
    this.renderTabs()
    if (!lead) {
      clearTimeout(this.loadTimer)
      this.chatTarget.replaceChildren()
      if (this.hasActionsTarget) this.actionsTarget.replaceChildren()
      this.loadedId = null
      return
    }

    if (row || this.loadedId !== lead.leadId) this.showLead(lead)
    if (!this.isOpen()) this.cancelPendingLoad()
    else if (this.loadedId !== lead.leadId) this.scheduleLoad(lead)
  }

  // A detached lead has no row to read from; its header fills in once its content loads.
  detachedLead() {
    if (!this.detached || this.detached.id !== this.selectedId) return null
    return { leadId: this.detached.id, leadChatUrl: this.detached.url, leadName: "", leadCompany: "", leadStatus: "", leadStatusColor: "", leadPicture: "" }
  }

  showLead({ leadName, leadCompany, leadStatus, leadStatusColor, leadPicture }) {
    this.leadNameTarget.textContent = leadName
    if (this.hasLeadCompanyTarget) this.leadCompanyTarget.textContent = leadCompany || ""
    this.leadStatusTarget.textContent = leadStatus
    this.leadStatusDotTarget.style.backgroundColor = leadStatusColor
    if (this.hasLeadPictureTarget) {
      const picture = this.leadPictureTarget
      picture.hidden = !leadPicture
      picture.onerror = () => {
        if (picture.dataset.fallback && picture.src !== picture.dataset.fallback) picture.src = picture.dataset.fallback
      }
      if (leadPicture && picture.getAttribute("src") !== leadPicture) picture.src = leadPicture
    }
  }

  // The loaded content describes its lead, which keeps the header current after a change and fills it for a lead
  // opened from a link.
  showLoadedLead(frame) {
    const summary = frame.querySelector("template[data-sidebar-lead]")
    if (!summary) return
    const { sidebarLead, name, company, status, statusColor, picture } = summary.dataset
    this.showLead({ leadId: sidebarLead, leadName: name, leadCompany: company, leadStatus: status, leadStatusColor: statusColor, leadPicture: picture })
  }

  // Waits briefly so holding j only loads the chat of the lead the selection stops on.
  scheduleLoad(lead) {
    clearTimeout(this.loadTimer)
    this.loadedId = lead.leadId
    if (this.hasActionsTarget) this.actionsTarget.replaceChildren()
    this.chatTarget.replaceChildren(this.loadingIndicator())
    this.loadTimer = setTimeout(() => this.load(lead.leadId, lead.leadChatUrl), LOAD_DELAY)
  }

  // Closing the sidebar before a scheduled load fires skips it; opening again schedules a new one.
  cancelPendingLoad() {
    if (!this.loadTimer) return
    clearTimeout(this.loadTimer)
    this.loadTimer = null
    this.loadedId = null
  }

  load(id, url) {
    this.loadTimer = null
    const frame = document.createElement("turbo-frame")
    frame.id = `lead-history-chat-${id}`
    frame.addEventListener("turbo:frame-load", event => {
      if (event.target !== frame || id !== this.selectedId) return
      this.showLoadedLead(frame)
      const actions = frame.querySelector("[data-sidebar-actions]")
      if (actions && this.hasActionsTarget) {
        actions.hidden = false
        this.actionsTarget.replaceChildren(actions)
      }
      this.restoreDrafts()
      this.renderTabs()
      this.scrollToLatest()
    })
    frame.src = url
    this.chatTarget.replaceChildren(frame)
  }

  loadingIndicator() {
    const indicator = document.createElement("p")
    indicator.className = "py-8 text-center text-gray-400"
    indicator.textContent = "…"
    return indicator
  }

  conversationVisible() {
    return this.isExpanded() || this.visibleTab() === "conversation"
  }

  scrollToLatest() {
    this.pendingHistoryScroll = true
    if (!this.conversationVisible()) return
    const history = this.chatTarget.querySelector("[data-sidebar-history]")
    if (!history) return
    history.scrollTop = history.scrollHeight
    this.pendingHistoryScroll = false
  }
}
